import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { DinningPromotionDetailScreenProps } from './type';
import { useIsDesktop } from '../../hooks/useIsDeskTop';
import { DinningLayout } from '../../WebLayout';
import {
  Image,
  StyleSheet,
  Text,
  View,
  TouchableOpacity,
  ScrollView,
  useWindowDimensions,
} from 'react-native';
import { STYLE_VARS } from '../../constants/style-vars';
import { COLORS, FONTS } from '../../constants';
import { Button } from '../../components/Button';
import { IS_WEB } from '../../constants/platform';
import { addBase64Prefix } from '../../services/addBase64Prefix';
import { share_icon } from '../../assets/base64';
import { useSelector } from 'react-redux';
import { RootState } from '../../services/redux';
import { IPromotion } from '../../types/promotions.types';
import { PromotionsApi } from '../../services/api';
import { Loading } from '../../components';
import PageTitle from '../../components/PageTitle';
import { Footer } from '../../components/Footer';
import RenderHTML from 'react-native-render-html';

const DinningPromotionDetail: FC<DinningPromotionDetailScreenProps> = ({
  navigation,
  route,
}) => {
  const promotionId = route.params.id;
  const isDesktop = useIsDesktop();

  const [promotion, setPromotion] = useState<IPromotion>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { width } = useWindowDimensions();

  const kccHeader = useMemo(() => {
    return (
      <View style={styles.headerWrapper}>
        <View style={styles.lineHeader} />
        <View style={{ paddingHorizontal: 10 }}>
          <Text style={styles.headerText}>The KCC</Text>
        </View>
        <View style={styles.lineHeader} />
      </View>
    );
  }, []);

  const getPromotion = useCallback(async (id: number) => {
    try {
      setIsLoading(true);
      const res = await PromotionsApi.getPromotion(id);
      setPromotion(res.data);
    } catch (error: any) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    getPromotion(promotionId);
  }, [promotionId, getPromotion]);

  if (isDesktop()) {
    return (
      <DinningLayout
        title="Dining Promotions"
        icon={require('../../assets/dining-promotions.png')}
        current={2}
        haveGoBackButton={true}
      >
        {isLoading === false && promotion ? (
          <View
            style={[
              styles.borderDarkGrey,
              {
                paddingHorizontal: 20,
                paddingVertical: 30,
              },
            ]}
          >
            {!promotion.poster && promotion.thumb && (
              <Image
                source={{ uri: promotion?.thumb }}
                style={{
                  width: '100%',
                  height: 500,
                  resizeMode: 'cover',
                  marginBottom: 10,
                }}
              />
            )}
            <Text style={[styles.titleText, { fontSize: 24 }]}>
              {promotion?.title}
            </Text>
            <View style={styles.titleSpan} />
            {promotion.content && (
              <Text
                style={{
                  fontFamily: FONTS.AvenirMedium,
                  fontSize: 15,
                  fontWeight: '500',
                  textAlign: 'justify',
                  color: COLORS.DARKER_GREY,
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  marginBottom: 20,
                }}
              >
                <RenderHTML
                  contentWidth={width / 2.05}
                  source={{ html: promotion.content }}
                />
              </Text>
            )}
            {promotion?.poster && (
              <Image
                source={{ uri: promotion?.poster || '' }}
                style={{
                  width: '100%',
                  height: 1300,
                  resizeMode: 'cover',
                  marginVertical: 20,
                }}
              />
            )}
          </View>
        ) : isLoading === true ? (
          <Loading />
        ) : (
          <></>
        )}
      </DinningLayout>
    );
  }

  return (
    <ScrollView showsVerticalScrollIndicator={false}>
      <View style={styles.container}>
        <PageTitle
          title="Dining Promotions"
          icon={require('../../assets/dining-promotions.png')}
          haveGoBackButton={true}
        />
      </View>
      {isLoading === false && promotion ? (
        <View style={{ marginBottom: 30 }}>
          {!promotion.poster && promotion.thumb && (
            <View style={styles.container}>
              <Image
                source={{ uri: promotion.thumb || '' }}
                style={{
                  width: '100%',
                  height: 170,
                  resizeMode: 'cover',
                  marginBottom: 20,
                }}
              />
            </View>
          )}
          <View style={[styles.container, { marginTop: 4 }]}>
            <View style={styles.headerWrapper}>
              <Text style={styles.headerText}>{promotion.title}</Text>
            </View>
          </View>
          {promotion.content && (
            <View style={styles.container}>
              <RenderHTML
                contentWidth={width - 40}
                source={{ html: promotion.content }}
                tagsStyles={{
                  body: {
                    color: COLORS.DARKER_GREY,
                    fontFamily: FONTS.AvenirMedium,
                  },
                  p: {
                    color: COLORS.DARKER_GREY,
                    fontFamily: FONTS.AvenirMedium,
                  },
                }}
              />
            </View>
          )}
          {promotion?.poster && (
            <View style={styles.container}>
              <Image
                source={{ uri: promotion.poster }}
                style={{
                  width: '100%',
                  height: 530,
                  resizeMode: 'cover',
                  marginVertical: 20,
                }}
              />
            </View>
          )}
        </View>
      ) : isLoading === true ? (
        <View style={{ marginBottom: 20 }}>
          <Loading />
        </View>
      ) : (
        <></>
      )}
      <Footer />
    </ScrollView>
  );
};

export default DinningPromotionDetail;

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 20,
  },
  borderDarkGrey: {
    borderColor: STYLE_VARS.fccDarkGrey,
    borderWidth: 0.5,
  },
  titleText: {
    fontFamily: FONTS.AvenirMedium,
    fontWeight: '500',
    color: STYLE_VARS.textColor,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 10,
  },
  titleSpan: {
    width: 30,
    height: 3,
    backgroundColor: STYLE_VARS.primaryColor,
    marginBottom: 15,
  },
  dateText: {
    fontFamily: FONTS.AvenirMedium,
    fontSize: 15,
    color: STYLE_VARS.fccDarkerGrey,
    marginBottom: 15,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  contentText: {
    fontFamily: FONTS.AvenirMedium,
    fontSize: 15,
    color: STYLE_VARS.fccDarkerGrey,
    marginBottom: 25,
    textTransform: 'uppercase',
  },
  headerWrapper: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerText: {
    fontFamily: FONTS.IowanOldStyleBTRoman,
    fontSize: 16,
    fontWeight: '500',
    color: STYLE_VARS.textColor,
    textTransform: 'uppercase',
    textAlign: 'center',
  },
  lineHeader: {
    width: 30,
    height: 1,
    backgroundColor: STYLE_VARS.primaryColor,
  },
});
